// What the app shows when a screen throws while rendering, instead of a blank page: React unmounts the whole tree on an
// uncaught render error, and the app had no boundary anywhere (review of 2 Oct 2026, S8). The shell wraps the routed screen in
// one, keyed by the address so moving to another page clears it and the sidebar stays usable; the app wraps everything in a
// last one. The panel uses plain links, so it renders with or without the router.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "./icons";

export function CrashPanel() {
  return (
    <section className="screen" id="s-error">
      <div className="content">
        <div className="page-head"><div><h1>Something went wrong</h1></div></div>
        <div className="panel">
          <div className="empty" role="alert">
            <Icon name="warning" />
            <h2>This page could not be shown</h2>
            <p>Something on it failed while it was being shown. Everything saved before is kept; anything typed on this page and not yet saved may need entering again.</p>
            <div className="row c gap-8">
              <a className="btn primary" href="/today">Back to Today</a>
              <button className="btn" type="button" onClick={() => window.location.reload()}>Reload</button>
            </div>
            <span className="help">If it happens again, tell your administrator what you were doing.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Shows `CrashPanel` in place of children that threw while rendering; a new `resetKey` (the address) tries them again. */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string | undefined }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console only: no patient data leaves the device in an error report.
    console.error(JSON.stringify({ level: "error", message: "screen failed to render", error: error.message, component: info.componentStack?.split("\n").find((l) => l.trim())?.trim() ?? null }));
  }

  override componentDidUpdate(prev: { resetKey?: string | undefined }): void {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  override render(): ReactNode {
    return this.state.failed ? <CrashPanel /> : this.props.children;
  }
}
